import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Input, Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { Link } from 'react-router-dom';
import cs_analysis_fetch from '../../utils/cs_analysis_fetch';
import { TicketDrawer, CreateTicketModal } from '../StoreDashboard/ClientHealthTab/ClientComms';
import TaskCreateModal from '../StoreDashboard/ClientHealthTab/TaskCreateModal';
import { task_prefill_from_ticket, ticket_store_id } from '../StoreDashboard/ClientHealthTab/task_ticket_links';
import { PRIVATE_LOCK_TITLE, is_raiser_private } from '../StoreDashboard/ClientHealthTab/ticket_visibility';
import TriageStrip from '../StoreDashboard/ClientHealthTab/TriageStrip';
import { NEEDS_TRIAGE_FILTER, is_merged, is_pending_triage, merged_into_label, pending_triage_rows, triage_query } from '../StoreDashboard/ClientHealthTab/ticket_triage';
import { enable_push, push_supported } from '../../utils/comms_push';

/**
 * Cross-client Client Tickets queue — the CSP-wide ticket cockpit
 * (cs-analysis design-client-comms.md; per-client work happens in
 * StoreDashboard's "Client Comms" tab, which every row deep-links into).
 * Mirrors the Product Tickets affordances that matter here: Mine/Team/All
 * scope, sort, org filter, owner column, create-with-org-picker.
 * Triage (2026-09-13): client-raised tickets land here PENDING, assigned to
 * the client's rep — the "Needs triage" pill queues them (server-side
 * ?triage=PENDING) and each row carries accept / merge / decline.
 */

const STATUS_COLORS = {
    OPEN: '#e67e22', IN_PROGRESS: '#2f6fed', WAITING_ON_CLIENT: '#8e44ad',
    WAITING_ON_PRODUCT: '#16a085', RESOLVED: '#27ae60', CLOSED: '#7f8c8d', DECLINED: '#c0392b',
};
const PRIORITY_COLORS = { URGENT: '#c0392b', HIGH: '#e67e22' };
const PRIORITY_ORDER = { URGENT: 0, HIGH: 1, NORMAL: 2, LOW: 3 };
// Title Case for enum display everywhere (Nikhil 2026-07-14: no lowercase labels).
const label = (s) => (s || '').split('_').map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : '')).join(' ');
const fmt_ts = (d) => { try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };

const S = {
    chip: (active, dark) => ({
        border: '1px solid ' + (active ? (dark ? '#2c3e50' : '#2f6fed') : '#d5dbe1'),
        background: active ? (dark ? '#2c3e50' : '#2f6fed') : '#fff',
        color: active ? '#fff' : '#4a5866',
        borderRadius: 14, padding: '3px 13px', fontSize: 12, cursor: 'pointer', lineHeight: '18px',
    }),
    control: { fontSize: 12.5, height: 32, borderRadius: 6, border: '1px solid #d8dfe6' },
    card: { background: '#fff', border: '1px solid #e5e9ee', borderRadius: 10 },
    th: { fontSize: 10.5, letterSpacing: 0.8, textTransform: 'uppercase', color: '#8a97a5', fontWeight: 700, padding: '9px 14px', borderBottom: '1px solid #e5e9ee', textAlign: 'left', whiteSpace: 'nowrap' },
    td: { fontSize: 13, color: '#33414e', padding: '10px 14px', borderTop: '1px solid #f1f4f7', verticalAlign: 'middle' },
};

export default function ClientTicketsQueue() {
    const [tickets, set_tickets] = useState(null);
    const [unread, set_unread] = useState([]);
    const [clients, set_clients] = useState([]);      // org picker + org filter
    const [reps_info, set_reps_info] = useState(null); // {role, self, reps} — scope + owner names
    const [scope, set_scope] = useState('all');       // mine | team | all
    const [status_filter, set_status_filter] = useState('active');
    const [triage_on, set_triage_on] = useState(false); // "Needs triage" pill → ?triage=PENDING
    const [module_filter, set_module_filter] = useState('');
    const [category_filter, set_category_filter] = useState('');
    const [priority_filter, set_priority_filter] = useState('');
    const [owner_filter, set_owner_filter] = useState('');
    const [org_filter, set_org_filter] = useState('');
    const [sort, set_sort] = useState({ key: 'updated', dir: 'desc' }); // header-click sorting
    const [selected, set_selected] = useState(null);  // {ticket, channelId} — in-page drawer
    const [q, set_q] = useState('');
    const [create_step, set_create_step] = useState(null); // null | 'pick' | {storeId, name}
    const [pick_q, set_pick_q] = useState('');
    const [task_for, set_task_for] = useState(null);      // ticket row → "Create task" from the row
    const [push_state, set_push_state] = useState(() => {
        if (!push_supported()) return 'unavailable';
        if (window.Notification && Notification.permission === 'denied') return 'denied';
        // 'granted' alone ≠ subscribed — index.js silently re-syncs, so treat
        // granted as subscribed-in-progress for the label, else off.
        return window.Notification && Notification.permission === 'granted' ? 'subscribed' : 'off';
    });
    const [error, set_error] = useState(null);

    const load = useCallback(async () => {
        try {
            const [tk, un] = await Promise.all([
                cs_analysis_fetch({ path: '/api/v1/tickets', query: triage_query(triage_on ? NEEDS_TRIAGE_FILTER : null) }),
                cs_analysis_fetch({ path: '/api/v1/comms/unread' }),
            ]);
            set_tickets((tk && tk.tickets) || []);
            set_unread((un && un.channels) || []);
            set_error(null);
        } catch (e) {
            set_error((e && e.message) || 'comms unavailable');
            set_tickets([]);
        }
    }, [triage_on]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
    useEffect(() => {
        cs_analysis_fetch({ path: '/api/v1/comms/clients' }).then((r) => set_clients((r && r.clients) || [])).catch(() => {});
        cs_analysis_fetch({ path: '/api/v1/cs/reps' }).then((r) => set_reps_info(r)).catch(() => set_reps_info({ reps: [], self: null }));
    }, []);

    // Open a ticket by id in the drawer (deep links; "Merged into CT-n" chips).
    const open_by_id = (id) => {
        if (!id) return;
        cs_analysis_fetch({ path: `/api/v1/tickets/${encodeURIComponent(String(id))}` })
            .then((r) => set_selected({ ticket: r.ticket, channelId: r.channelId }))
            .catch(() => {});
    };
    // Deep link (?open=<ticket id>) — GChat cross-notifications land here.
    useEffect(() => {
        open_by_id(new URLSearchParams(window.location.search).get('open'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const on_enable_push = async () => {
        try {
            const r = await enable_push();
            set_push_state(r === 'subscribed' ? 'subscribed' : r);
        } catch (e) { set_push_state('unavailable'); }
    };

    const me_id = reps_info && reps_info.self && reps_info.self.userId;
    const team_ids = new Set(((reps_info && reps_info.reps) || []).map((r) => r.userId));
    const rep_name = (uid) => {
        const r = ((reps_info && reps_info.reps) || []).find((x) => x.userId === uid);
        return r ? r.name : null;
    };
    const has_team = reps_info && reps_info.role !== 'rep' && team_ids.size > 1;

    const needle = q.trim().toLowerCase();
    const passes_base = (t) => {
        // Server-side pill; the local guard keeps the previous list's rows out
        // between the pill click and the ?triage=PENDING response.
        if (triage_on && !is_pending_triage(t)) return false;
        if (status_filter === 'active' && ['CLOSED', 'DECLINED'].includes(t.status)) return false;
        if (status_filter === 'closed' && !['CLOSED', 'DECLINED'].includes(t.status)) return false;
        if (module_filter && t.module !== module_filter) return false;
        if (category_filter && t.category !== category_filter) return false;
        if (priority_filter && t.priority !== priority_filter) return false;
        if (owner_filter === '__unassigned' && t.assigneeStaffId) return false;
        if (owner_filter && owner_filter !== '__unassigned' && t.assigneeStaffId !== owner_filter) return false;
        if (org_filter && !(t.client && t.client.name === org_filter)) return false;
        if (!needle) return true;
        return [t.title, t.client && t.client.name, `ct-${t.ref}`, t.module, t.category, t.status]
            .some((f) => (f || '').toString().toLowerCase().includes(needle));
    };
    const in_scope = (t, sc) =>
        sc === 'all' ? true
            : sc === 'mine' ? (!me_id || t.assigneeStaffId === me_id || t.raisedById === me_id)
                : (team_ids.has(t.assigneeStaffId) || team_ids.has(t.raisedById));
    const base = (tickets || []).filter(passes_base);
    const scope_counts = { mine: 0, team: 0, all: base.length };
    for (const t of base) {
        if (in_scope(t, 'mine')) scope_counts.mine++;
        if (in_scope(t, 'team')) scope_counts.team++;
    }
    // No ??/?. in this repo — CRA webpack4 can't minify them.
    const sort_val = (t, key) => {
        if (key === 'ref') return t.ref || 0;
        if (key === 'client') return ((t.client && t.client.name) || '').toLowerCase();
        if (key === 'title') return (t.title || '').toLowerCase();
        if (key === 'module') return t.module || '';
        if (key === 'priority') return t.priority in PRIORITY_ORDER ? PRIORITY_ORDER[t.priority] : 9;
        if (key === 'status') return t.status || '';
        if (key === 'owner') return (rep_name(t.assigneeStaffId) || (t.assigneeStaffId ? 'zz' : 'zzz')).toLowerCase();
        if (key === 'raisedBy') return (t.raisedByName || '').toLowerCase();
        return new Date(t.updatedAt).getTime();
    };
    const visible = base.filter((t) => in_scope(t, scope)).sort((a, b) => {
        const va = sort_val(a, sort.key);
        const vb = sort_val(b, sort.key);
        const d = va < vb ? -1 : va > vb ? 1 : new Date(b.updatedAt) - new Date(a.updatedAt);
        return sort.dir === 'asc' ? d : -d;
    });
    const on_sort = (key) => set_sort((s) => ({
        key,
        dir: s.key === key ? (s.dir === 'asc' ? 'desc' : 'asc') : (key === 'updated' || key === 'ref' ? 'desc' : 'asc'),
    }));
    const org_options = [...new Set((tickets || []).map((t) => t.client && t.client.name).filter(Boolean))].sort();
    // Inline quick transitions (Product-Tickets parity). Resolve/decline need
    // inputs — those route into the workspace instead.
    const quick_status = async (t, v) => {
        if (!v || v === t.status) return;
        if (v === '__resolve' || v === '__decline') { set_selected({ ticket: t, channelId: t.channelId }); return; }
        try {
            await cs_analysis_fetch({ path: `/api/v1/tickets/${t.id}/status`, method: 'POST', body: { status: v, expectedVersion: t.version } });
        } catch (e) { window.alert((e && e.message) || 'transition failed'); }
        load();
    };
    const pick_matches = pick_q.trim().length >= 2
        ? clients.filter((c) => c.name.toLowerCase().includes(pick_q.trim().toLowerCase())).slice(0, 12)
        : [];

    return (
        <div style={{ padding: '22px 30px', background: '#f6f8fa', minHeight: 'calc(100vh - 60px)' }}>
            <style>{'.ctq-row:hover{background:#f6f9ff !important}'}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, color: '#233240' }}>Client Tickets</h4>
                <button type='button' style={{ ...S.chip(true, false), fontWeight: 600 }} onClick={() => { set_pick_q(''); set_create_step('pick'); }}>
                    + New ticket
                </button>
                <div style={{ display: 'flex', gap: 5 }}>
                    {[['mine', 'My tickets'], ...(has_team ? [['team', 'Team']] : []), ['all', 'All']].map(([sc, lbl]) => (
                        <button key={sc} type='button' style={S.chip(scope === sc, true)}
                            onClick={() => set_scope(sc)}>
                            {lbl}{tickets ? ` (${scope_counts[sc]})` : ''}
                        </button>
                    ))}
                </div>
                <span style={{ width: 1, height: 18, background: '#e3e8ec' }} />
                <div style={{ display: 'flex', gap: 5 }}>
                    {['active', 'closed', 'all'].map((f) => (
                        <button key={f} type='button' style={S.chip(status_filter === f, false)}
                            onClick={() => set_status_filter(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</button>
                    ))}
                </div>
                {(() => {
                    // Client-raised tickets waiting for the rep's accept / merge / decline
                    // (server-side ?triage=PENDING when on; the count reads the loaded list).
                    const n = triage_on ? (tickets || []).length : pending_triage_rows(tickets).length;
                    return (
                        <button type='button' title='Client-raised tickets waiting for triage — accept, merge into an existing ticket, or decline'
                            onClick={() => set_triage_on((v) => !v)}
                            style={{
                                ...S.chip(triage_on, false),
                                ...(triage_on ? { background: '#b9770e', border: '1px solid #b9770e' }
                                    : n ? { background: '#fff8e6', border: '1px solid #e6b35a', color: '#8a6d1d', fontWeight: 600 } : {}),
                            }}>
                            <span role='img' aria-label='needs triage'>⚖</span> Needs triage{n ? ` · ${n}` : ''}
                        </button>
                    );
                })()}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center', fontSize: 11.5 }}>
                    {unread.length > 0 ? (
                        <Link to='/home/chat' title='Unread client conversations — open Chat'
                            style={{ fontSize: 12, textDecoration: 'none' }}>
                            💬 {unread.length} unread conversation{unread.length > 1 ? 's' : ''} →
                        </Link>
                    ) : null}
                    {push_state === 'subscribed' ? (
                        <span style={{ color: '#27ae60' }}>🔔 notifications on</span>
                    ) : push_state === 'denied' ? (
                        <span style={{ color: '#a4b0bb' }}>notifications blocked in browser</span>
                    ) : (
                        <button type='button' style={S.chip(false, false)} onClick={on_enable_push}
                            disabled={push_state === 'unavailable'}>
                            🔔 Enable notifications
                        </button>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <Input bsSize='sm' placeholder='Search title / client / CT-ref…' value={q}
                    onChange={(e) => set_q(e.target.value)} style={{ ...S.control, maxWidth: 260 }} />
                <Input type='select' bsSize='sm' value={org_filter} onChange={(e) => set_org_filter(e.target.value)} style={{ ...S.control, maxWidth: 200 }}>
                    <option value=''>All orgs</option>
                    {org_options.map((n) => <option key={n} value={n}>{n}</option>)}
                </Input>
                <Input type='select' bsSize='sm' value={module_filter} onChange={(e) => set_module_filter(e.target.value)} style={{ ...S.control, maxWidth: 150 }}>
                    <option value=''>All modules</option>
                    {['DESIGN', 'RENDER', 'PRESENTATION', 'CATALOGUE', 'PRICING', 'PRODUCTION', 'MES', 'OTHER'].map((m) => <option key={m} value={m}>{label(m)}</option>)}
                </Input>
                <Input type='select' bsSize='sm' value={priority_filter} onChange={(e) => set_priority_filter(e.target.value)} style={{ ...S.control, maxWidth: 140 }}>
                    <option value=''>All priorities</option>
                    {['LOW', 'NORMAL', 'HIGH', 'URGENT'].map((m) => <option key={m} value={m}>{label(m)}</option>)}
                </Input>
                <Input type='select' bsSize='sm' value={category_filter} onChange={(e) => set_category_filter(e.target.value)} style={{ ...S.control, maxWidth: 150 }}>
                    <option value=''>All categories</option>
                    {['BUG', 'FEATURE_REQUEST', 'TRAINING', 'CATALOG_CONFIG', 'QUESTION'].map((c) => <option key={c} value={c}>{label(c)}</option>)}
                </Input>
                <Input type='select' bsSize='sm' value={owner_filter} onChange={(e) => set_owner_filter(e.target.value)} style={{ ...S.control, maxWidth: 160 }}>
                    <option value=''>All owners</option>
                    <option value='__unassigned'>Unassigned</option>
                    {((reps_info && reps_info.reps) || []).map((r) => <option key={r.userId} value={r.userId}>{r.name}</option>)}
                </Input>
                {tickets ? (
                    <span style={{ fontSize: 11.5, color: '#8a97a5' }}>
                        {visible.length} of {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
                    </span>
                ) : null}
            </div>

            {error ? <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>Comms unavailable: {error}</div> : null}

            <div style={{ ...S.card, overflow: 'hidden' }}>
                {tickets === null ? (
                    <div style={{ padding: 40, textAlign: 'center' }}><Spinner size='sm' /></div>
                ) : visible.length === 0 ? (
                    <div style={{ padding: '48px 20px', textAlign: 'center' }}>
                        <div style={{ fontSize: 30, marginBottom: 8 }}>🎫</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#4a5866' }}>
                            No {status_filter !== 'all' ? status_filter : ''} client tickets{needle || org_filter || module_filter || priority_filter || scope !== 'all' ? ' match these filters' : ''}
                        </div>
                        <div style={{ fontSize: 12.5, color: '#a4b0bb', marginTop: 4 }}>
                            Tickets raised by clients or the team appear here — use "+ New ticket", or raise one from any conversation.
                        </div>
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                        <thead><tr>
                            {[['ref', 'Ref'], ['client', 'Client'], ['title', 'Title'], ['module', 'Module'], ['priority', 'Priority'], ['status', 'Status'], ['owner', 'Owner'], ['raisedBy', 'Raised by'], ['updated', 'Updated']].map(([k, lbl]) => (
                                <th key={k} onClick={() => on_sort(k)} title='Sort'
                                    style={{ ...S.th, cursor: 'pointer', userSelect: 'none', ...(k === 'title' ? { width: '32%' } : {}), ...(sort.key === k ? { color: '#2f6fed' } : {}) }}>
                                    {lbl}{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                </th>
                            ))}
                        </tr></thead>
                        <tbody>
                            {visible.map((t) => (
                                <tr key={t.id} className='ctq-row' style={{ cursor: 'pointer', background: '#fff' }}
                                    onClick={() => set_selected({ ticket: t, channelId: t.channelId })}>
                                    <td style={{ ...S.td, whiteSpace: 'nowrap', color: '#64748b', fontWeight: 600 }}>CT-{t.ref}</td>
                                    <td style={S.td} onClick={(e) => e.stopPropagation()}>
                                        {t.client ? (
                                            <Link to={`/home/store_dashboard/${t.client.storeId}/comms`}>{t.client.name}</Link>
                                        ) : '—'}
                                    </td>
                                    <td style={{ ...S.td, maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.title}>
                                        {is_raiser_private(t) ? <span role='img' aria-label='private to raiser' title={PRIVATE_LOCK_TITLE} style={{ marginRight: 5, fontSize: 12 }}>🔒</span> : null}
                                        {t.title}{t.clientVisible === false ? <Badge color='dark' style={{ marginLeft: 6 }}>internal</Badge> : null}
                                        {/* Tasks ↔ ticket (refinement A): linked-task count when the list
                                            row carries linkedActionables, and a row-level "Create task"
                                            (needs the row's client.storeId — the actionables API is store-keyed). */}
                                        {Array.isArray(t.linkedActionables) && t.linkedActionables.length > 0 ? (
                                            <span title={t.linkedActionables.map((a) => `${a.title || ''} · ${a.status || ''}`).join('\n')}
                                                style={{ marginLeft: 6, fontSize: 10.5, color: '#2f6fed', background: '#eef2fb', borderRadius: 10, padding: '1px 7px', fontWeight: 600 }}>
                                                ☑ {t.linkedActionables.length} task{t.linkedActionables.length === 1 ? '' : 's'}
                                            </span>
                                        ) : null}
                                        {ticket_store_id(t) && !['CLOSED', 'DECLINED'].includes(t.status) ? (
                                            <button type='button' title='Create a task for this ticket (linked; completes when the ticket is done)'
                                                onClick={(e) => { e.stopPropagation(); set_task_for(t); }}
                                                style={{ marginLeft: 6, fontSize: 10.5, color: '#2f6fed', background: 'transparent', border: '1px solid #c9d6f2', borderRadius: 10, padding: '0 7px', cursor: 'pointer', fontWeight: 600, lineHeight: '16px' }}>
                                                + task
                                            </button>
                                        ) : null}
                                        {is_merged(t) ? (
                                            <span role='button' title='Open the ticket this one was merged into'
                                                onClick={(e) => { e.stopPropagation(); open_by_id(t.mergedIntoId); }}
                                                style={{ marginLeft: 6, fontSize: 10.5, color: '#6c4bb5', background: '#f3efff', border: '1px solid #ddd2f6', borderRadius: 10, padding: '1px 7px', fontWeight: 600, cursor: t.mergedIntoId ? 'pointer' : 'default' }}>
                                                ⇢ {merged_into_label(t)}
                                            </span>
                                        ) : null}
                                        {is_pending_triage(t) ? (
                                            <TriageStrip compact ticket={t} store_id={ticket_store_id(t)} on_changed={load} />
                                        ) : null}
                                    </td>
                                    <td style={S.td}>{label(t.module)}</td>
                                    <td style={{ ...S.td, color: PRIORITY_COLORS[t.priority] || '#33414e', fontWeight: PRIORITY_COLORS[t.priority] ? 600 : 400 }}>{label(t.priority)}</td>
                                    <td style={S.td} onClick={(e) => e.stopPropagation()}>
                                        {['CLOSED', 'DECLINED', 'RESOLVED'].includes(t.status) ? (
                                            <Badge style={{ backgroundColor: STATUS_COLORS[t.status] || '#95a5a6', color: '#fff', padding: '4px 10px', borderRadius: 11, fontSize: 10.5 }}>{label(t.status)}</Badge>
                                        ) : (
                                            <Input type='select' bsSize='sm' value={t.status} title='Quick status change'
                                                onChange={(e) => quick_status(t, e.target.value)}
                                                style={{ ...S.control, height: 27, fontSize: 11.5, width: 158, fontWeight: 600, color: STATUS_COLORS[t.status] || '#33414e' }}>
                                                {['OPEN', 'IN_PROGRESS', 'WAITING_ON_CLIENT', 'WAITING_ON_PRODUCT']
                                                    .filter((s) => s === t.status || s !== 'OPEN')
                                                    .map((s) => <option key={s} value={s}>{label(s)}</option>)}
                                                <option value='__resolve'>resolve…</option>
                                                <option value='__decline'>decline…</option>
                                            </Input>
                                        )}
                                    </td>
                                    <td style={{ ...S.td, fontSize: 12 }}>
                                        {t.assigneeStaffId ? (rep_name(t.assigneeStaffId) || 'Infurnia') : <span style={{ color: '#c2ccd4' }}>—</span>}
                                    </td>
                                    <td style={{ ...S.td, fontSize: 12 }}>{t.raisedByName || '—'}{t.raisedByType === 'CLIENT_USER' ? ' (client)' : ''}</td>
                                    <td style={{ ...S.td, fontSize: 12, whiteSpace: 'nowrap', color: '#8a97a5' }}>{fmt_ts(t.updatedAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* org picker → CreateTicketModal */}
            <Modal isOpen={create_step === 'pick'} toggle={() => set_create_step(null)}>
                <ModalHeader toggle={() => set_create_step(null)}>New client ticket — pick the org</ModalHeader>
                <ModalBody>
                    <Input autoFocus placeholder='Type an org name…' value={pick_q}
                        onChange={(e) => set_pick_q(e.target.value)} style={{ ...S.control, height: 34 }} />
                    <div style={{ marginTop: 8, maxHeight: 300, overflowY: 'auto' }}>
                        {pick_q.trim().length < 2 ? (
                            <div style={{ fontSize: 12, color: '#a4b0bb', padding: '6px 2px' }}>Type at least 2 characters to search {clients.length} orgs.</div>
                        ) : pick_matches.length === 0 ? (
                            <div style={{ fontSize: 12, color: '#a4b0bb', padding: '6px 2px' }}>No org matches "{pick_q.trim()}".</div>
                        ) : pick_matches.map((c) => (
                            <div key={c.id} onClick={() => set_create_step({ storeId: c.storeId, name: c.name })}
                                style={{ padding: '7px 10px', fontSize: 13, cursor: 'pointer', borderRadius: 6 }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f2f6ff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}>
                                {c.name}
                                {!c.commsEnabled ? <span style={{ fontSize: 10.5, color: '#a4b0bb', marginLeft: 8 }}>chat not enabled</span> : null}
                            </div>
                        ))}
                    </div>
                </ModalBody>
            </Modal>
            {create_step && create_step !== 'pick' ? (
                <CreateTicketModal store_id={create_step.storeId} open
                    on_close={() => set_create_step(null)}
                    on_created={() => { set_create_step(null); load(); }} />
            ) : null}

            {/* row-level "Create task" → TaskCreateModal saving through from-client-ticket */}
            {task_for ? (
                <TaskCreateModal
                    open
                    store_id={ticket_store_id(task_for)}
                    reps={(reps_info && reps_info.reps) || []}
                    rep_role={(reps_info && reps_info.role) || 'rep'}
                    self_email={(reps_info && reps_info.self && reps_info.self.email) || ''}
                    from_client_ticket={task_for}
                    prefill={task_prefill_from_ticket(task_for)}
                    on_close={() => set_task_for(null)}
                    on_created={() => { set_task_for(null); load(); }}
                />
            ) : null}

            {selected ? (
                <TicketDrawer ticket={selected.ticket} channel_id={selected.channelId}
                    store_id={ticket_store_id(selected.ticket)}
                    on_close={() => set_selected(null)}
                    on_changed={async () => {
                        await load();
                        try {
                            const r = await cs_analysis_fetch({ path: `/api/v1/tickets/${selected.ticket.id}` });
                            set_selected({ ticket: r.ticket, channelId: r.channelId });
                        } catch (e) { set_selected(null); }
                    }} />
            ) : null}
        </div>
    );
}
